import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { ChecklistItem } from '@/types'

interface ChecklistEditorProps {
  /** List of checklist items to display/edit */
  items: ChecklistItem[]
  /** Called when any item changes */
  onChange: (items: ChecklistItem[]) => void
  /** Whether the editor is in read-only mode */
  readOnly?: boolean
}

/**
 * Reusable component for editing asset checklist items.
 * Shows each asset with a checkbox and optional notes field.
 */
export function ChecklistEditor({
  items,
  onChange,
  readOnly = false,
}: ChecklistEditorProps) {
  const handleCheckChange = (index: number, checked: boolean) => {
    const newItems = [...items]
    newItems[index] = { ...newItems[index], checked }
    onChange(newItems)
  }

  const handleNotesChange = (index: number, notes: string) => {
    const newItems = [...items]
    newItems[index] = { ...newItems[index], notes: notes || undefined }
    onChange(newItems)
  }

  if (items.length === 0) {
    return (
      <div className="text-muted-foreground text-sm py-4 text-center">
        No assets in this checklist.
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {items.map((item, index) => (
        <ChecklistItemRow
          key={item.asset_id}
          item={item}
          onCheckChange={(checked) => handleCheckChange(index, checked)}
          onNotesChange={(notes) => handleNotesChange(index, notes)}
          readOnly={readOnly}
        />
      ))}
    </div>
  )
}

interface ChecklistItemRowProps {
  item: ChecklistItem
  onCheckChange: (checked: boolean) => void
  onNotesChange: (notes: string) => void
  readOnly: boolean
}

function ChecklistItemRow({
  item,
  onCheckChange,
  onNotesChange,
  readOnly,
}: ChecklistItemRowProps) {
  const checkboxId = `checklist-${item.asset_id}`

  return (
    <div className="flex flex-col gap-2 p-3 border rounded-lg">
      <div className="flex items-center gap-3">
        <Checkbox
          id={checkboxId}
          checked={item.checked}
          onCheckedChange={(checked) =>
            onCheckChange(checked === true)
          }
          disabled={readOnly}
        />
        <Label
          htmlFor={checkboxId}
          className={`flex-1 cursor-pointer ${
            item.checked ? 'text-muted-foreground line-through' : ''
          } ${readOnly ? 'cursor-default' : ''}`}
        >
          {item.asset_name}
        </Label>
        {item.checked && (
          <CheckedIndicator />
        )}
      </div>
      {!readOnly && (
        <div className="pl-7">
          <Input
            placeholder="Add notes (optional)"
            value={item.notes ?? ''}
            onChange={(e) => onNotesChange(e.target.value)}
          />
        </div>
      )}
      {readOnly && item.notes && (
        <p className="pl-7 text-sm text-muted-foreground break-words">{item.notes}</p>
      )}
    </div>
  )
}

function CheckedIndicator() {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
      Done
    </span>
  )
}
